import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { CHAT_SOCKET_URL } from "../services/index";
import Services, { invalidateUserInfosCache } from "../services/Services";
import { useAuth } from "../AuthContext";
import { setStatusEmitter } from "../utils/statusBus";
import { classifyIncomingChatMessage } from "../utils/chatUnread";

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

const CHAT_TYPES = ["admin", "merchant", "customer"];

// Web Audio ile kısa bildirim sesi (asset gerekmez).
let _audioCtx = null;
function playNotify() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const ctx = _audioCtx;
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.12;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.12);
    });
  } catch (e) { /* sessizce geç */ }
}

export function ChatProvider({ children }) {
  const { userType, merchantCompanyID, merchantRole, logout } = useAuth();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const enabled = CHAT_TYPES.includes(userType) && !!token;

  const socketRef = useRef(null);
  const activeRoomRef = useRef(null);
  const msgListenersRef = useRef(new Set());
  const readListenersRef = useRef(new Set());
  const statusListenersRef = useRef(new Map()); // entity → Set<callback>
  const [ready, setReady] = useState(false);
  const [myUserId, setMyUserId] = useState(null);
  const [unreadByRoom, setUnreadByRoom] = useState({});
  const [presence, setPresence] = useState({}); // userId -> 'online'|'away'|'offline'

  // #5 — Kendi user kaydım değişince (admin firma/rol değiştirdi) yetki/firma değişimini
  // kontrol et; gerçekten değiştiyse oturumu kapatıp yeniden girişe yönlendir.
  const myUserIdRef = useRef(null);
  const userTypeRef = useRef(userType);
  userTypeRef.current = userType;
  const authRef = useRef({});
  authRef.current = { merchantCompanyID, merchantRole, logout };
  const checkOwnUserChange = useCallback(async () => {
    try {
      const res = await Services.GetUserInfos(token);
      const result = res?.data?.result;
      const info = Array.isArray(result) ? result[0] : result;
      if (!info) return;
      const newCorp = String(info?.corporationInfos?.corporationID ?? info?.corporationID ?? "").trim();
      const newRole = String(info?.role ?? "").trim();
      const curCorp = String(authRef.current.merchantCompanyID ?? "").trim();
      const curRole = String(authRef.current.merchantRole ?? "").trim();
      if (!curCorp && !curRole) return; // baseline yoksa karşılaştırma yapma (yanlış logout'u önle)
      const corpChanged = newCorp && curCorp && newCorp !== curCorp;
      const roleChanged = newRole && curRole && newRole !== curRole;
      if (corpChanged || roleChanged) {
        // "username" DA silinmeli: normal çıkış (Sidebar/Header/App) siliyor, burada
        // kalıyordu. LoginForm, username varsa koşulsuz /homepage'e yönlendirdiği için
        // giriş ekranı ile anasayfa arasında sonsuz yönlendirme döngüsü oluşuyordu.
        sessionStorage.removeItem("token");
        localStorage.removeItem("token");
        sessionStorage.removeItem("userID");
        localStorage.removeItem("userID");
        localStorage.removeItem("username");
        sessionStorage.removeItem("username");
        // Ad/soyad da temizlenmeli (Sidebar/Header/App çıkışlarındaki gibi): AuthContext.logout
        // localStorage'a dokunmadığı için yeni kayıt+otomatik girişte önceki kullanıcının adı
        // header/anasayfada görünüyordu.
        localStorage.removeItem("name");
        localStorage.removeItem("lastname");
        try { authRef.current.logout?.(); } catch (e) {}
        window.location.href = "/";
      }
    } catch (e) { /* sessizce geç */ }
  }, [token]);
  const checkOwnUserChangeRef = useRef(() => {});
  checkOwnUserChangeRef.current = checkOwnUserChange;

  const refreshUnread = useCallback(() => {
    if (!enabled) return;
    Services.ChatUnread(token)
      .then((res) => {
        const rooms = res?.data?.result?.rooms || [];
        const map = {};
        rooms.forEach((r) => { map[r.room_id] = r.unread; });
        setUnreadByRoom(map);
      })
      .catch(() => {});
  }, [enabled, token]);

  const setActiveRoom = useCallback((roomId) => { activeRoomRef.current = roomId || null; }, []);

  const markRoomRead = useCallback((roomId) => {
    if (!roomId) return;
    setUnreadByRoom((prev) => (prev[roomId] ? { ...prev, [roomId]: 0 } : prev));
    if (enabled) {
      const at = Date.now();
      Services.ChatMarkRead(token, roomId).catch(() => {});
      if (socketRef.current) socketRef.current.emit("read", { roomId, at });
    }
  }, [enabled, token]);

  const emitRelay = useCallback((roomId, message) => {
    if (socketRef.current) socketRef.current.emit("relay", { roomId, message });
  }, []);

  // oda açılınca anlık presence anlık görüntüsü iste
  const requestPresence = useCallback((roomId) => {
    if (!socketRef.current) return;
    socketRef.current.emit("presence:get", { roomId }, (map) => {
      if (map && typeof map === "object") setPresence((prev) => ({ ...prev, ...map }));
    });
  }, []);

  const subscribe = useCallback((cb) => { msgListenersRef.current.add(cb); return () => msgListenersRef.current.delete(cb); }, []);
  const subscribeRead = useCallback((cb) => { readListenersRef.current.add(cb); return () => readListenersRef.current.delete(cb); }, []);

  const subscribeStatus = useCallback((entity, cb) => {
    const map = statusListenersRef.current;
    if (!map.has(entity)) map.set(entity, new Set());
    map.get(entity).add(cb);
    return () => map.get(entity)?.delete(cb);
  }, []);

  const emitStatusUpdate = useCallback((data) => {
    socketRef.current?.emit("status:update", data);
  }, []);

  // Modül seviyesindeki bus'a bağlı emitter'ı kaydet → hook'suz dosyalar da emit edebilir.
  useEffect(() => {
    setStatusEmitter(emitStatusUpdate);
    return () => setStatusEmitter(null);
  }, [emitStatusUpdate]);

  useEffect(() => {
    if (!enabled) { setReady(false); return; }
    refreshUnread();
    const socket = io(CHAT_SOCKET_URL, { transports: ["websocket"], auth: { token }, reconnection: true });
    socketRef.current = socket;

    const sendVisibility = () => {
      socket.emit("visibility", { state: document.visibilityState === "hidden" ? "away" : "online" });
    };

    socket.on("ready", (d) => { setReady(true); setMyUserId(d?.userID || null); myUserIdRef.current = d?.userID || null; sendVisibility(); });
    socket.on("disconnect", () => setReady(false));

    socket.on("presence:update", ({ userId, state }) => {
      if (!userId) return;
      setPresence((prev) => ({ ...prev, [userId]: state || "offline" }));
    });

    socket.on("message:new", (data) => {
      const roomId = data?.roomId;
      if (!roomId) return;
      // Kural utils/chatUnread.js'te (BE ile aynı): ekip arkadaşının karşı tarafa cevabı
      // okunmamış sayılmaz ve odayı bu kullanıcı için de okunmuş yapar; kendi mesajım sayılmaz.
      const kind = classifyIncomingChatMessage(roomId, data?.message, myUserIdRef.current, userTypeRef.current);
      if (kind === "team") {
        setUnreadByRoom((prev) => (prev[roomId] ? { ...prev, [roomId]: 0 } : prev));
      } else if (kind === "other") {
        if (activeRoomRef.current !== roomId) {
          setUnreadByRoom((prev) => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
        }
        playNotify();
      }
      msgListenersRef.current.forEach((cb) => { try { cb(data); } catch (e) {} });
    });

    socket.on("read:update", (data) => {
      readListenersRef.current.forEach((cb) => { try { cb(data); } catch (e) {} });
    });

    socket.on("status:update", (data) => {
      const entity = data?.entity;
      if (!entity) return;
      invalidateUserInfosCache(); // FE-35: başka kullanıcının değişikliği → kullanıcı bilgisi tazelensin
      // Kendi user kaydım ya da firmam değiştiyse yetki/firma değişimini kontrol et (#5).
      // Kontrol gate'li olduğundan gereksiz tetikleme zararsız (sadece gerçek değişimde logout).
      if (
        (entity === "user" && data?.id != null && String(data.id) === String(myUserIdRef.current)) ||
        (entity === "company" && data?.id != null && String(data.id) === String(authRef.current.merchantCompanyID ?? ""))
      ) {
        checkOwnUserChangeRef.current?.();
      }
      const listeners = statusListenersRef.current.get(entity);
      if (listeners) listeners.forEach((cb) => { try { cb(data); } catch (e) {} });
    });

    document.addEventListener("visibilitychange", sendVisibility);

    return () => {
      document.removeEventListener("visibilitychange", sendVisibility);
      socket.close();
      socketRef.current = null;
      setReady(false);
    };
  }, [enabled, token, refreshUnread]);

  const totalUnread = useMemo(
    () => Object.values(unreadByRoom).reduce((a, b) => a + (b || 0), 0),
    [unreadByRoom]
  );

  const value = useMemo(
    () => ({ enabled, ready, myUserId, unreadByRoom, totalUnread, presence, refreshUnread, setActiveRoom, markRoomRead, emitRelay, requestPresence, subscribe, subscribeRead, subscribeStatus, emitStatusUpdate }),
    [enabled, ready, myUserId, unreadByRoom, totalUnread, presence, refreshUnread, setActiveRoom, markRoomRead, emitRelay, requestPresence, subscribe, subscribeRead, subscribeStatus, emitStatusUpdate]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
